"use client";

import type { ReactNode } from "react";
import { AtomicSearchInterface } from "@coveo/atomic-react";
import type { SearchEngine } from "@coveo/headless";
import { useLocale, useMessages } from "next-intl";
import { toCoveoUILanguage } from "src/utils/coveoUtils";

export type AtomicPageWrapperProps = {
  engine: SearchEngine;
  reflectStateInUrl: boolean;
  fieldsToInclude?: string[];
  children?: ReactNode;
};

const DEFAULT_FIELDS_TO_INCLUDE = [
  "title",
  "page_og_title",
  "page_og_description",
  "page_og_image",
];

const COVEO_KEY_PREFIX = "coveo-";

// Walks the next-intl messages tree (which may be nested under a site
// namespace per `i18n/request.ts`) and pulls out every `coveo-*` leaf string
// for the Coveo i18next bundle. Strips the `coveo-` prefix to match Coveo's
// internal i18n keys.
const collectCoveoLocalization = (
  messages: Record<string, unknown>,
): Record<string, string> => {
  const result: Record<string, string> = {};

  const walk = (node: Record<string, unknown>) => {
    for (const [key, value] of Object.entries(node)) {
      if (typeof value === "string") {
        if (key.startsWith(COVEO_KEY_PREFIX)) {
          result[key.slice(COVEO_KEY_PREFIX.length)] = value;
        }
      } else if (value && typeof value === "object") {
        walk(value as Record<string, unknown>);
      }
    }
  };

  walk(messages);
  return result;
};

export const AtomicPageWrapper = (props: AtomicPageWrapperProps) => {
  const locale = useLocale();
  const messages = useMessages() as Record<string, unknown>;
  const coveoLang = toCoveoUILanguage(locale);
  const coveoLocalization = collectCoveoLocalization(messages);

  const fieldsToInclude = [
    ...(props.fieldsToInclude ?? []),
    ...DEFAULT_FIELDS_TO_INCLUDE,
  ];

  if (!props.children) return null;

  return (
    <AtomicSearchInterface
      iconAssetsPath=""
      engine={props.engine}
      reflectStateInUrl={props.reflectStateInUrl}
      fieldsToInclude={fieldsToInclude}
      language={coveoLang || "en-US"} //accepts 2 characters language code in most cases. Locales such as "en-US" is not supported
      localization={(i18) => {
        i18.addResourceBundle(coveoLang, "translation", coveoLocalization);
      }}
      logLevel="trace"
    >
      {props.children}
    </AtomicSearchInterface>
  );
};
